import React, { useState, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { TooltipIcon } from '@web/components';
import { Input, Radio, MultiSelect, Dropdown } from '@web/atoms';
import { getYears, getMonths, getDayList } from '../../pets/functions';
import { Footer } from '../Footer';
import { FormFieldsContainer } from '../FormFieldsContainer';
import { Question } from '../Question';
import { ApiResult } from '../../api';
import { useReporter } from '../../reporter';
import { onboardingQuestionSubmitted, onboardingCTAClicked } from '../events';
import { useGetBreeds } from '../../breeds';
import {
  FieldWrapper,
  StyledDropdownGroup,
  StyledHeader,
  StyledForm,
} from './styles.js';
import { schema } from './schema';

export { schema } from './schema';

export const AboutForm = ({
  defaultValuesGender,
  defaultValuesBirthday,
  genderList,
  goBack,
  labelName,
  index,
  onSubmit,
  pupInfoQuestionCaption,
  pupInfoQuestionPrompt,
  labelYear,
  labelMonth,
  labelDay,
  pupBirthdayQuestionCaption,
  pupBirthdayQuestionPrompt,
  breedQuestionPrompt,
  defaultValuesBreeds,
  labelBreed,
  weightLabel,
  weightQuestionPrompt,
  idealWeightLabel,
  puppyIdealWeightLabel,
  isPuppy,
  spayedChipLabel,
  spayedStatusQuestionCaption,
  spayedStatusQuestionPrompt,
  unspayedChipLabel,
  idealWeightHelp,
  defaultValuesCalories,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitted, isSubmitSuccessful },
    reset,
    getValues,
    trigger,
  } = useForm({
    defaultValues: {
      ...defaultValuesGender,
      ...defaultValuesBirthday,
      ...defaultValuesBreeds,
      ...defaultValuesCalories,
    },
    resolver: yupResolver(schema),
    mode: 'onTouched',
  });

  const questionRefs = useRef([]);
  questionRefs.current = new Array(5)
    .fill(null)
    .map((_, i) => questionRefs.current[i] || React.createRef());

  const getBreeds = useGetBreeds();

  const [optionsYear, setOptionsYear] = useState([]);
  const [optionsMonth, setOptionsMonth] = useState([]);
  const [optionsDay, setOptionsDay] = useState([]);
  const refreshYearList = useCallback(() => {
    const year = getYears();
    setOptionsYear(year);
  }, []);

  const refreshMonthList = useCallback(() => {
    const month = getMonths(getValues('year'));
    setOptionsMonth(month);
  }, [getValues]);

  const refreshDayList = useCallback(() => {
    const data = getDayList(getValues('year'), getValues('month'), labelDay);
    setOptionsDay(data);
  }, [getValues, labelDay]);

  useEffect(() => {
    if (Object.keys(errors).length > 0) {
      const element = document.querySelectorAll('[role="alert"]');
      //scroll to error
      element[0].scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [errors]);

  useEffect(() => {
    refreshYearList();
    refreshMonthList();
    refreshDayList();
  }, [refreshDayList, refreshMonthList, refreshYearList]);
  const reporter = useReporter();
  const idealWeightCopy = isPuppy ? puppyIdealWeightLabel : idealWeightLabel;

  const idealWeightTooltip = (
    <TooltipIcon
      name="Display weight information"
      variant="info"
      placement="top"
    >
      {idealWeightHelp}
    </TooltipIcon>
  );

  const handleBlurOrChange = questionIndex => {
    if (questionIndex < questionRefs.current.length - 1) {
      // Scroll to the next question
      questionRefs.current[questionIndex + 1].current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };
  const handleQuestionSubmit = position => {
    reporter.tag(onboardingQuestionSubmitted(position));
  };
  const submitForm = async data => {
    reporter.tag(onboardingCTAClicked(1));
    const result = await onSubmit(data);

    ApiResult.ifError(result, () =>
      setTimeout(() =>
        reset(getValues(), {
          keepIsSubmitted: false,
        }),
      ),
    );
  };
  return (
    <StyledForm onSubmit={handleSubmit(submitForm)}>
      <StyledHeader>About your pup</StyledHeader>
      <FormFieldsContainer>
        <Question
          caption={pupInfoQuestionCaption}
          prompt={pupInfoQuestionPrompt}
          bgColor={'neutral.white'}
          ref={questionRefs.current[0]}
        >
          <Controller
            control={control}
            name="gender"
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <>
                <Radio
                  {...field}
                  choices={genderList}
                  legendText={labelName}
                  name="gender"
                  error={error?.message || ''}
                  status={error?.message ? 'error' : null}
                />
              </>
            )}
          />
          <FieldWrapper>
            <Controller
              control={control}
              name="name"
              render={({ field, fieldState }) => (
                <Input
                  {...field}
                  visuallyHideLabel
                  labelText={labelName}
                  error={fieldState.error?.message}
                  onBlur={() => {
                    field.onBlur(); // Important: Call the original onBlur function
                    if (getValues().gender) handleBlurOrChange(0);
                    handleQuestionSubmit(1); // only scroll to the next question if they have selected a gender
                  }}
                  status={fieldState.error ? 'error' : null}
                  value={field.value || ''}
                  maxLength={100}
                />
              )}
            />
          </FieldWrapper>
        </Question>
        <Question
          prompt={pupBirthdayQuestionPrompt}
          caption={pupBirthdayQuestionCaption}
          bgColor="neutral.white"
          ref={questionRefs.current[1]}
        >
          <StyledDropdownGroup>
            <Controller
              name="year"
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Dropdown
                  {...field}
                  showLabel={false}
                  noSelectionLabel={labelYear}
                  label={labelYear}
                  onChange={e => {
                    field.onChange(e);
                    refreshMonthList();
                  }}
                  selected={field?.value ? field.value.toString() : ''}
                  error={error?.message}
                  options={optionsYear}
                />
              )}
            />
            <Controller
              name="month"
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Dropdown
                  {...field}
                  showLabel={false}
                  noSelectionLabel={labelMonth}
                  label={labelMonth}
                  onChange={e => {
                    field.onChange(e);
                    refreshDayList();
                    handleQuestionSubmit(2);
                    // trigger('year'); // TODO see if we can reintroduce this without causing infinite loop on mobile. Related to downshift https://ollie.atlassian.net/browse/ES-897 https://github.com/downshift-js/downshift/issues/1511
                  }}
                  disabled={optionsMonth.length === 0}
                  selected={field?.value ? field.value.toString() : ''}
                  error={error?.message}
                  options={optionsMonth}
                />
              )}
            />
            <Controller
              name="day"
              control={control}
              render={({ field: { ref, ...field }, fieldState: { error } }) => {
                let selected = field?.value ? field.value.toString() : '';
                if (selected.length === 1) selected = `0${selected}`; // This for some reason became necessary as part of this ticket https://ollie.atlassian.net/browse/ES-897
                return (
                  <Dropdown
                    {...field}
                    showLabel={false}
                    noSelectionLabel={labelDay}
                    label={labelDay}
                    onChange={val => {
                      field.onChange(val);
                      handleBlurOrChange(1, true);
                      // trigger('month'); // TODO see if we can reintroduce this without causing infinite loop on mobile. Related to downshift https://ollie.atlassian.net/browse/ES-897 https://github.com/downshift-js/downshift/issues/1511
                    }}
                    disabled={optionsDay.length === 0}
                    selected={selected}
                    error={error?.message}
                    options={optionsDay}
                  />
                );
              }}
            />
          </StyledDropdownGroup>
        </Question>
        <Question
          prompt={breedQuestionPrompt}
          bgColor="neutral.white"
          ref={questionRefs.current[2]}
        >
          <Controller
            name="breeds"
            control={control}
            render={({ field: { ref, ...field } }) => (
              <MultiSelect
                {...field}
                searchLabel={labelBreed}
                values={getBreeds}
                onChange={val => {
                  field.onChange(val);
                  handleBlurOrChange(2);
                  handleQuestionSubmit(3);
                  // trigger('month'); // TODO see if we can reintroduce this without causing infinite loop on mobile. Related to downshift https://ollie.atlassian.net/browse/ES-897 https://github.com/downshift-js/downshift/issues/1511
                }}
                selectedValues={field.value}
                max={{ count: 2, message: 'Maximum of two breeds allowed' }}
              />
            )}
          />
        </Question>

        <Question
          prompt={weightQuestionPrompt}
          bgColor="neutral.white"
          ref={questionRefs.current[3]}
        >
          <Controller
            control={control}
            name="weight"
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Input
                {...field}
                type="number"
                labelText={weightLabel}
                value={field.value || ''}
                status={error?.message ? 'error' : null}
                error={error?.message || ''}
                onBlur={val => {
                  field.onChange(val);
                }}
                visuallyHideLabel
              />
            )}
          />

          <Controller
            control={control}
            name="idealWeight"
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <Input
                {...field}
                type="number"
                labelText={idealWeightCopy}
                icon={idealWeightTooltip}
                value={field.value || ''}
                status={error?.message ? 'error' : null}
                error={error?.message || ''}
                onBlur={val => {
                  field.onChange(val);
                  handleBlurOrChange(3);
                  // trigger('month'); // TODO see if we can reintroduce this without causing infinite loop on mobile. Related to downshift https://ollie.atlassian.net/browse/ES-897 https://github.com/downshift-js/downshift/issues/1511
                }}
                visuallyHideLabel
              />
            )}
          />
        </Question>

        <Question
          prompt={spayedStatusQuestionPrompt}
          caption={spayedStatusQuestionCaption}
          bgColor="neutral.white"
          ref={questionRefs.current[4]}
        >
          <Controller
            control={control}
            name="spayedStatus"
            render={({ field: { ref, ...field }, fieldState: { error } }) => (
              <>
                <Radio
                  {...field}
                  legendText="spayed status"
                  value={`${field.value}`}
                  choices={[
                    { name: spayedChipLabel, value: 'true' },
                    { name: unspayedChipLabel, value: 'false' },
                  ]}
                  error={error?.message || ''}
                  status={error?.message ? 'error' : null}
                  onChange={e => {
                    field.onChange(e);
                    trigger(field.name);
                    handleBlurOrChange(4);
                    handleQuestionSubmit(4);
                  }}
                />
              </>
            )}
          />
        </Question>
      </FormFieldsContainer>
      <Footer
        onBackButtonClick={goBack}
        isBackButtonDisabled={
          isSubmitting || (isSubmitted && isSubmitSuccessful)
        }
        isForwardButtonDisabled={
          isSubmitting || (isSubmitted && isSubmitSuccessful)
        }
        isForwardButtonLoading={
          isSubmitting || (isSubmitted && isSubmitSuccessful)
        }
      />
    </StyledForm>
  );
};

AboutForm.displayName = 'AboutForm';

AboutForm.propTypes = {
  defaultValuesGender: PropTypes.shape({
    gender: PropTypes.string,
    name: PropTypes.string,
  }).isRequired,
  genderList: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  goBack: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  labelName: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  pupInfoQuestionCaption: PropTypes.string.isRequired,
  pupInfoQuestionPrompt: PropTypes.string.isRequired,
  labelYear: PropTypes.string.isRequired,
  labelMonth: PropTypes.string.isRequired,
  labelDay: PropTypes.string.isRequired,
  defaultValuesBirthday: PropTypes.shape({}).isRequired,
  pupBirthdayQuestionCaption: PropTypes.string.isRequired,
  pupBirthdayQuestionPrompt: PropTypes.string.isRequired,
  defaultValuesBreeds: PropTypes.shape({ breeds: PropTypes.array }).isRequired,
  breedQuestionPrompt: PropTypes.string.isRequired,
  labelBreed: PropTypes.string.isRequired,
  defaultValuesCalories: PropTypes.shape({
    weight: PropTypes.number,
    idealWeight: PropTypes.number,
    spayedStatus: PropTypes.bool,
  }).isRequired,
  weightLabel: PropTypes.string.isRequired,
  weightQuestionPrompt: PropTypes.string.isRequired,
  idealWeightLabel: PropTypes.string.isRequired,
  isPuppy: PropTypes.bool.isRequired,
  spayedChipLabel: PropTypes.string.isRequired,
  spayedStatusQuestionCaption: PropTypes.string.isRequired,
  spayedStatusQuestionPrompt: PropTypes.string.isRequired,
  unspayedChipLabel: PropTypes.string.isRequired,
  puppyIdealWeightHelp: PropTypes.string.isRequired,
  puppySpayNeuterNotice: PropTypes.string.isRequired,
  idealWeightHelp: PropTypes.string.isRequired,
  puppyIdealWeightLabel: PropTypes.string.isRequired,
};
